import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type {
	NextEditorPlugin,
	OptionalPlugin,
} from '@atlaskit/editor-common/types/next-editor-plugin';
import type { CardPlugin } from '@atlaskit/editor-plugin-card/cardPluginType';
import type { TypeAheadPlugin } from '@atlaskit/editor-plugin-type-ahead/type-ahead-plugin-type';
import { PluginKey, TextSelection, type EditorState } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';
import type { HistorySuggestionTracker } from '@atlassian/conversation-assistant-instrumentation/history-suggestions/tracker';

import { linkifyInsertedText } from './linkifyInsertedText';

export type HistoryCompletionOptions = {
	/** Candidate identity is local-only and never included in analytics payloads. */
	getCompletion: (query: string) => string | { suffix: string; candidateId: string } | undefined;
	disabled?: boolean;
	tracking?: Pick<
		HistorySuggestionTracker,
		'displaySuggestions' | 'hideSuggestions' | 'accept' | 'confirmInsertion' | 'insertionFailed'
	>;
};

/** Registered once; history options can arrive or change without replacing the editor preset. */
export const historyCompletionPlugin: NextEditorPlugin<
	'historyCompletion',
	{
		pluginConfiguration: {
			getOptions: () => HistoryCompletionOptions | undefined;
			/** Successful acceptance is a user edit; dismissal is not. */
			onAccept?: () => void;
		};
		dependencies: [TypeAheadPlugin, OptionalPlugin<CardPlugin>];
	}
> = ({ config, api }) => ({
	name: 'historyCompletion',
	pmPlugins: () => [
		{
			name: 'historyCompletion',
			plugin: () => {
				const dismissalKey = new PluginKey<boolean>('historyCompletion');
				let view: EditorView | undefined;
				let composing = false;
				let refreshFrame: number | undefined;
				const getActiveOptions = () => {
					const options = config.getOptions();
					return options?.disabled ? undefined : options;
				};
				const getSuggestion = (state: EditorState) => {
					const options = getActiveOptions();
					const { doc, selection } = state;
					const paragraph = doc.firstChild;
					if (
						!options ||
						!view?.hasFocus() ||
						!view.editable ||
						composing ||
						view.composing ||
						!(selection instanceof TextSelection) ||
						!selection.empty ||
						doc.childCount !== 1 ||
						paragraph?.type.name !== 'paragraph' ||
						selection.from !== doc.content.size - 1 ||
						api?.typeAhead?.actions.isOpen(state) ||
						dismissalKey.getState(state)
					) {
						return undefined;
					}
					let plainText = true;
					paragraph.forEach((node) => {
						if (!node.isText || node.marks.length > 0) {
							plainText = false;
						}
					});
					const query = doc.textContent;
					if (!plainText || !query.trim() || /^[\/@]/u.test(query.trimStart())) {
						return undefined;
					}
					const completion = options.getCompletion(query);
					const suffix = typeof completion === 'string' ? completion : completion?.suffix;
					return suffix
						? {
								suffix,
								position: selection.from,
								// Match title deduplication without tying a display to a conversation ID.
								// This text is only compared locally, never passed to the analytics tracker.
								comparisonKey: (query + suffix).trim().toLowerCase(),
								candidateId: typeof completion === 'string' ? undefined : completion?.candidateId,
								tracking: options.tracking,
							}
						: undefined;
				};
				// Reporting belongs to the installed view, never speculative decoration reads.
				let displayed:
					| {
							candidateId: string;
							comparisonKey: string;
							displayId: string;
							tracking: NonNullable<HistoryCompletionOptions['tracking']>;
					  }
					| undefined;
				const safelyTrack = <T>(callback: () => T): T | undefined => {
					try {
						return callback();
					} catch {
						// Analytics is optional and must not break editing. Never log completion text.
						return undefined;
					}
				};
				const hideDisplay = () => {
					const previous = displayed;
					displayed = undefined;
					if (previous) {
						safelyTrack(() => previous.tracking.hideSuggestions(previous.displayId));
					}
				};
				const updateDisplay = (editor: EditorView) => {
					const suggestion = getSuggestion(editor.state);
					if (!suggestion?.candidateId || !suggestion.tracking) {
						hideDisplay();
						return;
					}
					const { candidateId, comparisonKey, tracking } = suggestion;
					if (displayed?.tracking !== tracking || displayed?.comparisonKey !== comparisonKey) {
						hideDisplay();
					}
					// Keep the original opaque ID while equivalent titles swap cache entries.
					const displayCandidateId = displayed?.candidateId ?? candidateId;
					const displayId = safelyTrack(() => tracking.displaySuggestions([displayCandidateId]));
					if (displayId) {
						displayed = { candidateId: displayCandidateId, comparisonKey, displayId, tracking };
					}
				};
				const refresh = (editor: EditorView) => {
					if (refreshFrame !== undefined) {
						cancelAnimationFrame(refreshFrame);
						refreshFrame = undefined;
					}
					if (getActiveOptions()) {
						refreshFrame = requestAnimationFrame(() => {
							refreshFrame = undefined;
							if (!editor.isDestroyed) {
								editor.dispatch(editor.state.tr.setMeta('historyCompletionRefresh', true));
							}
						});
					}
					return false;
				};
				return new SafePlugin<boolean>({
					key: dismissalKey,
					state: {
						init: () => false,
						apply(tr, previous) {
							// Acceptance edits the document too, so explicit dismissal takes precedence.
							if (tr.getMeta(dismissalKey) === true) {
								return true;
							}
							return tr.docChanged || !getActiveOptions() ? false : previous;
						},
					},
					props: {
						decorations(state) {
							const suggestion = getSuggestion(state);
							if (!suggestion) {
								return DecorationSet.empty;
							}
							return DecorationSet.create(state.doc, [
								Decoration.widget(
									suggestion.position,
									(editor) => {
										const widget = editor.dom.ownerDocument.createElement('span');
										widget.className = 'placeholder-decoration';
										widget.setAttribute('contenteditable', 'false');
										widget.setAttribute('data-testid', 'history-ghost-text');
										// Match editor autocomplete: keep unaccepted ghost text hidden from screen readers.
										// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/platform/packages/editor/editor-plugin-autocomplete/src/pm-plugins/ghost-text-decoration.ts#lines-22:29
										widget.setAttribute('aria-hidden', 'true');
										widget.textContent = suggestion.suffix;
										return widget;
									},
									{ side: 1, key: `history-completion-${suggestion.suffix}` },
								),
							]);
						},
						handleKeyDown(editor, event) {
							const hasModifier = event.shiftKey || event.altKey || event.ctrlKey || event.metaKey;
							// The caret sits before the ghost widget at the end of the doc, so these moves
							// would land after it; ProseMirror then snaps the caret back, which flickers.
							// Cmd is allowed here (Cmd+ArrowDown/Right move to the end); other modifiers are not.
							const movesPastGhost =
								!event.shiftKey &&
								!event.altKey &&
								!event.ctrlKey &&
								(event.key === 'ArrowDown' || (event.key === 'ArrowRight' && event.metaKey));
							const acceptsCompletion =
								!hasModifier && (event.key === 'Tab' || event.key === 'ArrowRight');
							const dismissesWithEscape = !hasModifier && event.key === 'Escape';
							if (
								(!acceptsCompletion && !movesPastGhost && !dismissesWithEscape) ||
								event.isComposing ||
								event.keyCode === 229 ||
								event.defaultPrevented
							) {
								return false;
							}
							const suggestion = getSuggestion(editor.state);
							if (!suggestion) {
								return false;
							}
							if (movesPastGhost) {
								// Dismiss synchronously so the widget is gone before the browser moves the
								// caret, then let the default move proceed (no dead key, no scroll blocking).
								editor.dispatch(editor.state.tr.setMeta(dismissalKey, true));
								return false;
							}
							if (event.key === 'Escape') {
								// Consume Escape only while dismissing a visible suggestion.
								event.stopPropagation();
							}
							let tr = editor.state.tr;
							if (acceptsCompletion) {
								tr.insertText(suggestion.suffix, suggestion.position);
								// Linkify the whole paragraph: a URL can span the typed prefix and the suffix.
								tr = linkifyInsertedText(
									tr,
									1,
									suggestion.position + suggestion.suffix.length,
									api?.card?.commands.queueCardsFromRange,
								);
							}
							const tracking =
								displayed?.comparisonKey === suggestion.comparisonKey &&
								displayed?.tracking === suggestion.tracking
									? displayed?.tracking
									: undefined;
							// Capture the completed draft before dispatch: preserve the typed prefix's
							// casing/whitespace, and ensure insertion itself is never classified as an edit.
							const acceptanceId =
								acceptsCompletion && tracking
									? safelyTrack(() =>
											tracking.accept(
												tr.doc.textContent,
												1,
												event.key === 'Tab' ? 'tab' : 'arrowRight',
											),
										)
									: undefined;
							try {
								editor.dispatch(tr.setMeta(dismissalKey, true));
							} catch (error) {
								if (acceptanceId && tracking) {
									safelyTrack(() => tracking.insertionFailed(acceptanceId));
								}
								throw error;
							}
							if (acceptsCompletion) {
								if (acceptanceId && tracking) {
									safelyTrack(() =>
										tracking.confirmInsertion(acceptanceId, editor.state.doc.textContent),
									);
								}
								config.onAccept?.();
							}
							event.preventDefault();
							return true;
						},
						handleDOMEvents: {
							focus: refresh,
							blur: refresh,
							compositionstart: (editor) => {
								composing = true;
								return refresh(editor);
							},
							compositionend: (editor) => {
								composing = false;
								return refresh(editor);
							},
						},
					},
					view(editor) {
						view = editor;
						refresh(editor);
						return {
							update: updateDisplay,
							destroy() {
								hideDisplay();
								if (refreshFrame !== undefined) {
									cancelAnimationFrame(refreshFrame);
								}
								view = undefined;
							},
						};
					},
				});
			},
		},
	],
});
